<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  Field,
  Filter,
  fontOptions,
  OrientationOptions,
  PageSizeOptions,
  ReportDesign,
} from "../../types/report";
import Buttons from "../ui/form/Buttons.vue";
import TextInput from "../ui/form/TextInput.vue";
import SearchSelectMultiItems from "../ui/form/SearchSelectMultiItems.vue";
import StepField from "./DesignerComponents/StepField.vue";
import StepTable from "./DesignerComponents/StepTable.vue";
import StepPivot from "./DesignerComponents/StepPivot.vue";
import StepFilter from "./DesignerComponents/StepFilter.vue";
import QueryPreview from "./DesignerComponents/QueryPreview.vue";
import GridInput from "../ui/form/GridInput.vue";
import SearchSelect from "../ui/form/SearchSelect.vue";
import NumberInput from "../ui/form/NumberInput.vue";
import { isEmpty } from "../../utils/CommonFunctions";
import { useFormValidation } from "../../composables/useFormValidation";
import { required } from "../../utils/validation";
import { api } from "../../api/Api";
import Toast from "../../services/Toast";

const router = useRouter();

const report = ref<ReportDesign>({
  id: "",
  name: "",
  description: "",
  type: "",
  baseTable: "",
  joins: [],
  fields: [],
  pageSettings: {
    pageSize: "A4",
    topMargin: 5,
    bottomMargin: 5,
    leftMargin: 5,
    rightMargin: 5,
    footerMargin: 5,
    headerMargin: 5,
    orientation: "Portrait",
    fontSize: 12,
    fontFamily: "Arial",
  },
  rawReport: false,
  rawQuery: "",
  baseTableAlias: "",
  filters: [],
  groups: [],
  sorts: [],
  pivot: null,
  tables: [],
  modules: [],
  accessGroups: [],
});

const { fields, setFieldRules, validateForm, setFormData } = useFormValidation({
  name: "",
  id: "",
  uid: "",
  description: "",
  accessGroups: "",
  modules: "",
  fields: "",
  tables: "",
  pageSize: "",
  orientation: "",
  fontFamily: "",
  fontSize: 12,
  rawReport: false,
});

setFieldRules("name", [required("Report Name is required")]);
setFieldRules("accessGroups", [
  required("At least One Access Group is required"),
]);
setFieldRules("modules", [required("At least One Module is required")]);
setFieldRules("fields", [required("At Least One Field is required")]);
setFieldRules("tables", [required("At Least one Table is required")]);
setFieldRules("pageSize", [required("Page Size is required")]);
setFieldRules("orientation", [required("Orientation is required")]);
setFieldRules("fontFamily", [required("Font Family is required")]);
setFieldRules("fontSize", [required("Font Size is required")]);

const handleAddPivotField = (
  field: Field,
  pivotFieldId: string,
  aggregateFieldId: string,
  aggregateFunction: string,
  width: number,
  dbTable: string,
  dbField: string,
  sortField: string,
  sortOrder: "asc" | "desc",
  dynamicFields: boolean = false
) => {
  console.log("Adding pivot field:", field);
  if (report.value.pivot) {
    if (!isEmpty(field)) {
      field.sort_order = report.value.pivot.fields.length;
      report.value.pivot.fields.push(field);
    }
    report.value.pivot.pivotFieldId = pivotFieldId;
    report.value.pivot.aggregateFieldId = aggregateFieldId;
    report.value.pivot.aggregateFunction = aggregateFunction;
    report.value.pivot.width = width;
    report.value.pivot.dbTable = dbTable;
    report.value.pivot.dbField = dbField;
    report.value.pivot.sortField = sortField;
    report.value.pivot.sortOrder = sortOrder;
    report.value.pivot.dynamicFields = dynamicFields;
  } else if (!isEmpty(field)) {
    field.sort_order = 0; // Default sort order for the first field
    report.value.pivot = {
      pivotFieldId: pivotFieldId,
      aggregateFieldId: aggregateFieldId,
      aggregateFunction: aggregateFunction,
      dynamicFields: dynamicFields,
      width: width,
      fields: field ? [field] : [],
      dbTable: dbTable,
      dbField: dbField,
      sortField: sortField,
      sortOrder: sortOrder,
    };
  }
  console.log("Updated pivot:", report.value);
};

const handleAddFilter = (field: Filter) => {
  report.value.filters.push(field);
};
/*const handleTableUpdated = (table: any) => {
    if (table) {
        const existingTableIndex = report.value.tables.findIndex(t => t.id === table.id);
        if (existingTableIndex !== -1) {
            report.value.tables[existingTableIndex] = table;
        } else {
            report.value.tables.push(table);
        }
    }
    showTableScreen.value = false;
}*/
// Load report if editing existing one
onMounted(() => {
  const reportId = router.currentRoute.value.params.id;
  console.log("Report ID from route:", reportId);
  if (reportId) {
    api
      .post(`reports/record`, {
        id: reportId,
      })
      .then((response) => {
        if (response.success) {
          setFormData(response.data);
          report.value = JSON.parse(response.data.query);
          console.log("Loaded report:", report.value);
        } else {
          Toast.show(response.message || "Failed to load report", "error");
        }
      })
      .catch((error) => {
        console.error("Error loading report:", error);
        Toast.show("Error loading report", "error");
      });
  }
});

const saveReport = async () => {
  fields.value.tables.value =
    report.value.tables.length > 0 ? report.value.tables : null;
  fields.value.fields.value =
    report.value.fields.length > 0 ? report.value.fields : null;
  fields.value.pageSize.value = report.value.pageSettings.pageSize
    ? report.value.pageSettings.pageSize
    : "";
  fields.value.orientation.value = report.value.pageSettings.orientation
    ? report.value.pageSettings.orientation
    : "";
  fields.value.fontFamily.value = report.value.pageSettings.fontFamily
    ? report.value.pageSettings.fontFamily
    : "";
  fields.value.fontSize.value = report.value.pageSettings.fontSize
    ? report.value.pageSettings.fontSize
    : 12;
  fields.value.accessGroups.value =
    report.value.accessGroups.length > 0 ? report.value.accessGroups : null;
  fields.value.modules.value =
    report.value.modules.length > 0 ? report.value.modules : null;
  fields.value.name.value = report.value.name ? report.value.name : "";
  fields.value.description.value = report.value.description
    ? report.value.description
    : "";

  setFieldRules("fields", report.value.rawReport ? [] : [required("At Least One Field is required")]);
  setFieldRules("tables", report.value.rawReport ? [] : [required("At Least one Table is required")]);

  if (validateForm()) {
    const params = {
      name: report.value.name,
      description: report.value.description,
      accessGroups: report.value.accessGroups,
      modules: report.value.modules,
      report: JSON.stringify(report.value),
      uid: fields.value.uid.value,
    };
    const response = await api.post(
      "reports/" + (fields.value.uid.value ? "update" : "create"),
      params
    );
    if (response.success == true) {
      Toast.show("Report saved successfully", "info");
    } else {
      Toast.show(response.message || "Failed to save report", "error");
    }
  }
};

const addFieldsToReport = (field: Field) => {
  if (!isEmpty(field)) {
    field.sort_order = report.value.fields.length;
    if (field.id) {
      const existingIndex = report.value.fields.findIndex(
        (f) => f.id === field.id
      );
      if (existingIndex !== -1) {
        report.value.fields[existingIndex] = field;
      } else {
        report.value.fields.push(field);
      }
    }
  }
};

const reportRawQueryChanged = (query: string) => {
  report.value.rawQuery = query;
}
</script>

<template>
  <div class="report-designer">
    <div class="designer-header">
      <h1>{{ report && report.id ? "Edit Report" : "New Report" }}</h1>
      <div class="header-actions">
        <Buttons
          caption="Cancel"
          type="secondary"
          @click="router.push('/reports')"
        />
        <Buttons caption="Save" @click="saveReport" />
      </div>
    </div>

    <div class="designer-content">
      <div class="basic-info section">
        <div class="section-header">
          <div class="step-indicator">Step 1</div>
          <h2>Report Configurations</h2>
        </div>
        <div class="form-group">
          <TextInput
            label="Report Name"
            v-model="report.name"
            placeholder="Enter report name"
            :error="fields.name.touched ? fields.name.error : null"
            @blur="fields.name.touched = true"
          />
        </div>
        <!--<div class="form-group">
          <TextInput label = "Description" v-model="report.description" placeholder="Enter description" 
          :error="
              fields.description.touched ? fields.description.error : null
            "
            @blur="fields.description.touched = true"/>
        </div>-->
        <div class="form-group">
          <SearchSelectMultiItems
            label="Assigned Modules"
            v-model="report.modules"
            :error="fields.modules.touched ? fields.modules.error : null"
            @blur="fields.modules.touched = true"
            end-point="modules"
            placeholder="Select Module"
          />
        </div>
        <div class="form-group">
          <SearchSelectMultiItems
            label="Assigned Role Groups"
            v-model="report.accessGroups"
            :error="
              fields.accessGroups.touched ? fields.accessGroups.error : null
            "
            @blur="fields.accessGroups.touched = true"
            end-point="system/security/roles"
            placeholder="Select Group"
          />
        </div>
        <div class="form-group">
          <GridInput label="Page Settings">
            <div class="md:grid md:grid-cols-4 md:gap-6">
              <SearchSelect
                label="Page Size"
                v-model="report.pageSettings.pageSize"
                :error="fields.pageSize.touched ? fields.pageSize.error : null"
                @blur="fields.pageSize.touched = true"
                :options="PageSizeOptions"
                class="col-span-1"
              />
              <SearchSelect
                label="Orientation"
                v-model="report.pageSettings.orientation"
                :error="
                  fields.orientation.touched ? fields.orientation.error : null
                "
                @blur="fields.orientation.touched = true"
                :options="OrientationOptions"
                class="col-span-1"
              />
              <SearchSelect
                label="Font Family"
                v-model="report.pageSettings.fontFamily"
                :error="
                  fields.fontFamily.touched ? fields.fontFamily.error : null
                "
                @blur="fields.fontFamily.touched = true"
                :options="fontOptions"
                class="col-span-1"
              />
              <NumberInput
                label="Font Size"
                v-model="report.pageSettings.fontSize"
                :error="fields.fontSize.touched ? fields.fontSize.error : null"
                @blur="fields.fontSize.touched = true"
              />
              <div
                class="col-span-4 md:grid md:grid-cols-6 sm:grid-cols-4 xs:grid-cols-2 grid-cols-1 md:gap-2"
              >
                <NumberInput
                  label="Left Margin"
                  v-model="report.pageSettings.leftMargin"
                />
                <NumberInput
                  label="Right Margin"
                  v-model="report.pageSettings.rightMargin"
                />
                <NumberInput
                  label="Top Margin"
                  v-model="report.pageSettings.topMargin"
                />
                <NumberInput
                  label="Bottom Margin"
                  v-model="report.pageSettings.bottomMargin"
                />
                <NumberInput
                  label="Footer Margin"
                  v-model="report.pageSettings.footerMargin"
                />
                <NumberInput
                  label="Header Margin"
                  v-model="report.pageSettings.headerMargin"
                />
              </div>
            </div>
          </GridInput>
        </div>
      </div>

      <StepTable
        :report="report"
        @query-changed="reportRawQueryChanged"
        :error="fields.tables.error"
      />

      <StepField
        v-if="report.baseTable"
        :error="fields.fields.error"
        @addField="addFieldsToReport"
        :report="report"
      />

      <StepPivot
        v-if="
          report.baseTable &&
          report.fields.length > 0 &&
          report.fields.some(
            (f) =>
              f.type === 'number' &&
              f.name.toLocaleLowerCase() !== 'id' &&
              !f.name.toLocaleLowerCase().includes('_id')
          )
        "
        @addField="handleAddPivotField"
        :report="report"
      />

      <StepFilter
        v-if="(report.baseTable && report.fields.length > 0) || report.rawReport"
        @addField="handleAddFilter"
        :report="report"
      />

      <QueryPreview
        v-if="(report.baseTable && report.fields.length > 0) || report.rawReport"
        :report="report"
      />
    </div>
  </div>
</template>

<style>
.section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.section-header h2 {
  font-size: 1.25rem;
  color: #1a5fb4;
  margin: 0;
}

.step-indicator {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a5fb4;
  background: #e7f1ff;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.form-group label {
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
}
</style>
<style scoped>
.report-designer {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.designer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.designer-header h1 {
  font-size: 1.75rem;
  margin: 0;
  color: #212529;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.designer-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.form-group label {
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
}

.section-header h3 {
  font-size: 1rem;
  margin: 0;
  color: #495057;
}

.table-container,
.fields-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.join-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.join-info {
  flex: 1;
}

.join-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.join-type {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a5fb4;
  background-color: #e7f1ff;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.join-table {
  font-weight: 500;
  color: #495057;
}

.join-conditions {
  font-size: 0.875rem;
  color: #6c757d;
}

.condition-text {
  margin-top: 0.25rem;
  font-family: monospace;
}

.join-actions {
  display: flex;
  gap: 0.5rem;
}

.field-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.field-actions {
  display: flex;
  gap: 0.5rem;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  color: #6c757d;
}

:deep(.p-dropdown) {
  width: 100%;
}

:deep(.p-inputtext) {
  width: 100%;
}

@media (max-width: 768px) {
  .report-designer {
    padding: 1rem;
  }

  .designer-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions button {
    flex: 1;
  }

  .join-item {
    flex-direction: column;
    gap: 1rem;
  }

  .join-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
